import { FolderChecked, Ribbon } from 'icons';
import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { formatDateAsLocal } from '@/utils/date';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { itemByLanguage } from '@/utils/language';
import { getTimeSpentOnPlan } from '@/utils/time';

import Constants, { routes } from '@/constants';

import PlanCard from '../PlanCard';
import PlanCardIcon from '../PlanCardComponents/PlanCardIcon';
import PlanCardLabel from '../PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '../PlanCardComponents/PlanCardSubHeader';
import PlanCardTag from '../PlanCardComponents/PlanCardTag';
import PlanCardTitle from '../PlanCardComponents/PlanCardTitle';
import PlanCardContent from '../PlanCardContent/PlanCardContent';

import { Plan, PlanAssignment } from '@/_generated/api-clients/xp';

interface MyPlansPlanCardProps {
  plan: Plan;
  assignment?: PlanAssignment;
}

const MyPlansPlanCard = ({ plan, assignment }: MyPlansPlanCardProps) => {
  const { t } = useTranslation();
  const teamMemberPlanStatus = assignment;
  const isComplete =
    teamMemberPlanStatus?.status === Constants.TRAINING_PLANS.COMPLETED;
  const dueDate = new Date(teamMemberPlanStatus?.dueDate ?? '').getTime();
  const stepsComplete = teamMemberPlanStatus?.stepsComplete;
  const lastCompletedDate =
    teamMemberPlanStatus?.latestCompletionReport?.completionDate ??
    teamMemberPlanStatus?.completionDate;
  const percentComplete = plan.totalSteps
    ? Math.round(((stepsComplete ?? 0) / plan.totalSteps) * 100)
    : undefined;
  const timeSpentOnPlan = teamMemberPlanStatus
    ? getTimeSpentOnPlan({ ...teamMemberPlanStatus })
    : null;
  const stepCount = plan.totalSteps;
  const completionDate = teamMemberPlanStatus?.completionDate;

  return (
    <PlanCard
      category={plan.category}
      dataTestId="MyPlan"
      link={routes.plans.plan({ planId: plan.id })}
      progress={!isComplete && stepCount ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PlanCardLabel>
        <PlanCardTitle>{itemByLanguage(plan.name)}</PlanCardTitle>
        {isComplete ? (
          <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
            {generateProgressText({
              completedStepCount: stepsComplete,
              isComplete,
              timeSpentOnPlan: timeSpentOnPlan,
              stepCount,
            })}
          </PlanCardSubHeader>
        ) : (
          <PlanCardSubHeader data-testid="PlanTaskCount">
            {getProgressBarText({
              completedStepCount: stepsComplete,
              isComplete,
              percentComplete,
              stepCount,
              timeSpentOnPlan: timeSpentOnPlan,
            })}
          </PlanCardSubHeader>
        )}
      </PlanCardContent>
      {!isComplete && !!lastCompletedDate && (
        <PlanCardIcon
          icon={<FolderChecked className="size-6" />}
          tooltip={
            <p className="text-variant-body2 text-grayscale-gray7 font-normal">
              {t('TrainingPlans.statusOptions.lastCompleted')}{' '}
              {formatDateAsLocal(lastCompletedDate)}
            </p>
          }
        />
      )}
      {!isComplete && !!teamMemberPlanStatus?.dueDate && (
        <PlanCardTag
          color={getDueDateColor(dueDate)}
          text={getDueDateString(dueDate)}
          tooltip={t('TrainingPlans.accountability.due.dueDate', {
            dueDate: new Date(dueDate).toLocaleDateString(),
          })}
        />
      )}
      {isComplete && (
        <PlanCardIcon
          icon={<Ribbon className="size-7!" />}
          tooltip={
            <>
              <strong>{t('TrainingPlans.statusOptions.completed')}:</strong>
              {new Date(completionDate ?? '').toLocaleDateString()}
            </>
          }
        />
      )}
    </PlanCard>
  );
};

export default MyPlansPlanCard;
